/**
 * Modulo "Riga libera": aggiunge all'ordine un prodotto non a catalogo,
 * valido solo per quell'ordine (nome, prezzo, IVA, quantita').
 * Non crea un prodotto nel magazzino: la riga usa il prodotto segnaposto
 * dell'azienda (vedi lib/orders/customOrderLine.ts).
 */
import { useState } from 'react';
import { Plus, X, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import type { OrderItemInput } from '../../../lib/types/orders';
import { ManualOrderService } from '../../../lib/services/manualOrderService';
import { buildCustomOrderLine, validateCustomOrderLine } from '../../../lib/orders/customOrderLine';

interface CustomOrderLineFormProps {
  organizationId: string;
  onAddItem: (item: OrderItemInput) => void;
  disabled?: boolean;
}

const inputCls = 'w-full h-8 px-2 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white';

export function CustomOrderLineForm({ organizationId, onAddItem, disabled = false }: CustomOrderLineFormProps) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [vat, setVat] = useState('22');
  const [qty, setQty] = useState('1');
  const [saving, setSaving] = useState(false);

  const reset = () => { setName(''); setPrice(''); setVat('22'); setQty('1'); };

  const handleAdd = async () => {
    const draft = {
      name,
      unitPrice: parseFloat(price.replace(',', '.')),
      taxRate: parseFloat(vat.replace(',', '.')),
      quantity: parseFloat(qty.replace(',', '.')),
    };
    const error = validateCustomOrderLine(draft);
    if (error) { toast.error(error); return; }
    setSaving(true);
    try {
      const placeholderId = await ManualOrderService.getCustomLineProductId(organizationId);
      onAddItem(buildCustomOrderLine(draft, placeholderId));
      reset();
      setOpen(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Impossibile aggiungere la riga libera');
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className="inline-flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
      >
        <Plus className="w-4 h-4" />
        Aggiungi prodotto non a catalogo (solo per questo ordine)
      </button>
    );
  }

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-900 dark:text-white">Prodotto non a catalogo</span>
        <button type="button" onClick={() => { reset(); setOpen(false); }} className="p-1 text-gray-400 hover:text-gray-600" aria-label="Chiudi">
          <X className="w-4 h-4" />
        </button>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Vale solo per questo ordine: non viene aggiunto al magazzino e non scala giacenze.
      </p>
      <div className="grid grid-cols-12 gap-2">
        <label className="col-span-12 sm:col-span-6 text-xs text-gray-500 dark:text-gray-400">
          Nome
          <input className={inputCls} value={name} onChange={e => setName(e.target.value)} maxLength={255} autoFocus />
        </label>
        <label className="col-span-4 sm:col-span-2 text-xs text-gray-500 dark:text-gray-400">
          Quantità
          <input className={inputCls} type="number" min="0" step="any" value={qty} onChange={e => setQty(e.target.value)} />
        </label>
        <label className="col-span-4 sm:col-span-2 text-xs text-gray-500 dark:text-gray-400">
          Prezzo (€)
          <input className={inputCls} type="number" min="0" step="0.01" value={price} onChange={e => setPrice(e.target.value)} />
        </label>
        <label className="col-span-4 sm:col-span-2 text-xs text-gray-500 dark:text-gray-400">
          IVA %
          <input className={inputCls} type="number" min="0" max="100" step="any" value={vat} onChange={e => setVat(e.target.value)} />
        </label>
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleAdd}
          disabled={disabled || saving}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Aggiungi all'ordine
        </button>
      </div>
    </div>
  );
}

export default CustomOrderLineForm;
